import { useQuery } from "@tanstack/react-query"
import { useEffect, useMemo, useRef } from "react"

import useIsDesktop from "@hooks/useIsDesktop"
import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import fetchObjectListData from "@utils/fetchObjectListData"

import useObjectsView from "@organisms/ObjectsView/ObjectsView.context"
import ViewBar from "@organisms/ViewBar/ViewBar"

import { ObjectsViewTabProps } from "./ObjectsViewTab.types"

export default function ObjectsViewTab(props: ObjectsViewTabProps) {
    const {
        id,
        title,
        phase,
        countEndpoint,
        endpointKwargs,
        listEndpoint,
        isDefaultActiveTabForPhase,
        hide,
        hideCount,
    } = props

    const {
        activePhase,
        activeTab,
        setActiveTab,
        activeTabOverride,
        searchKeywords,
        getFiltersForTab,
        setActivePhase,
    } = useObjectsView()
    const { isLoading } = useUser()
    const isDesktop = useIsDesktop()
    const hasSetTabFromUrl = useRef(false)

    const [tabCountEndpoint, tabListEndpoint] = useSlugExtractor([countEndpoint, listEndpoint])

    // A badge counts for the tab it opens, so it takes that tab's own filters rather than the
    // active tab's — the two differ wherever a view setting narrows the list, such as the horizon.
    const tabFilters = useMemo(() => getFiltersForTab(title), [getFiltersForTab, title])

    const { data } = useQuery({
        // URLSearchParams hashes to an empty object, so the serialized form is what actually
        // keys the query; `tabFilters` itself is here for the exhaustive-deps rule.
        queryKey: [tabCountEndpoint, endpointKwargs, searchKeywords, tabFilters, tabFilters?.toString(), title],
        queryFn: () =>
            fetchObjectListData({
                objectName: `${title} Count`,
                endpoint: tabCountEndpoint,
                endpointKwargs: endpointKwargs,
                searchKeywords: searchKeywords,
                filters: tabFilters,
            }),
        staleTime: 60000,
        // The endpoint's company slug comes from the user, so the count waits for it. Mobile shows only the
        // selected tab's count (in the select trigger), so the other tabs don't fetch theirs, and neither does a
        // hidden tab.
        enabled: !isLoading && activePhase === phase && !hide && !hideCount && (isDesktop || activeTab?.id === id),
    })

    const onSetActiveTab = () => {
        setActivePhase(phase)

        // Saved as route templates, so a workspace switch never reuses another company's URLs.
        setActiveTab({
            id,
            title,
            phase,
            countEndpoint,
            endpointKwargs,
            listEndpoint,
        })
    }

    const isUrlActivated = activeTabOverride === title && !hasSetTabFromUrl.current

    const isLegacyActivated = typeof activeTab === "string" && activeTab === title

    const listEndpointHasChanged =
        activeTab?.listEndpoint !== tabListEndpoint &&
        !!activeTab?.listEndpoint &&
        activeTab?.id === id &&
        activeTab?.phase === phase

    const isDefaultActivated = !!isDefaultActiveTabForPhase && activePhase === phase && activeTab?.phase !== phase

    useEffect(() => {
        if (isLoading) {
            return
        }

        const shouldActivateTab = isUrlActivated || isLegacyActivated || isDefaultActivated || listEndpointHasChanged

        if (shouldActivateTab) {
            onSetActiveTab()

            hasSetTabFromUrl.current = true
        }
    }, [
        isLoading,
        activeTabOverride,
        id,
        activeTab,
        title,
        isDefaultActiveTabForPhase,
        activePhase,
        phase,
        onSetActiveTab,
        listEndpointHasChanged,
    ])

    return (
        <ViewBar.Tab
            value={id}
            label={title}
            phase={phase}
            count={hideCount ? undefined : data?.count}
            isSelected={activeTab?.id === id || isDefaultActivated}
            isHidden={hide}
            onSelect={onSetActiveTab}
        />
    )
}
